import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import FactorLensesContext from '../../contexts/factor-lenses-context';
import { useBulkFundData } from './useBulkFundData';
import type { AgGridReact } from 'ag-grid-react';
import { type AnalysisSubject, analyticsService, findInPortfolioBy, isCashNode } from 'venn-utils';
import { getRangeLogic } from '../analysisPeriodUtils';
import { type BulkManagementTableModalKey, type BulkManageRow, type ModalManager } from '../types';
import { isBulkManageAxisRow, isBulkManageFundRow, rowHasHistoricalError } from '../utils';
import type { RowClassRules, SelectionChangedEvent } from 'ag-grid-community';
import { compact } from 'lodash';
import { AxisRowClass } from '../columns';
import type { ProxyMetaWithName } from '../../modals/pickers/proxy-picker/BulkProxyPicker';
import type { FundToProxy } from 'venn-components';
import type { FundToBulkProxy } from '../../modals/pickers/types';
import moment, { now } from 'moment';
import { getPortfolioAllocDates, getPortfolioMaxAllocationDate } from './utils';

export function useModalManager<T extends string>(initial?: T): ModalManager<T> {
  const [activeModal, setActiveModal] = useState<T | null>(initial ?? null);

  const open = useCallback((key: T) => setActiveModal(key), []);
  const close = useCallback(() => setActiveModal(null), []);

  return useMemo(() => ({ activeModal, open, close }), [activeModal, open, close]);
}

type Props = {
  subject: AnalysisSubject;
  shouldHighlightRowsWithHistoricalErrors: boolean;
};
export const useBulkManagementTable = ({ subject, shouldHighlightRowsWithHistoricalErrors }: Props) => {
  const [showLiquidatedInvestments, setShowLiquidatedInvestments] = useState(true);

  const { primaryFactorLens } = useContext(FactorLensesContext);
  const {
    uniqFunds,
    rangeDataByFund,
    benchmarkRange,
    proxyDataByFund,
    holdingsProxyDataByFund,
    primaryRangeAnalysis,
    rangeAnalysis,
    benchmarkInfo,
    cashRateRange,
    referenceRateRange,
  } = useBulkFundData(subject);

  const gridRef = useRef<AgGridReact | null>(null);
  const [selectedFundIds, setSelectedFundIds] = useState<string[]>([]);
  const [openedSecondaryId, setOpenedSecondaryId] = useState<string | undefined>();

  const bulkManagementTableModals = useModalManager<BulkManagementTableModalKey>();

  useEffect(() => {
    if (
      bulkManagementTableModals.activeModal !== 'returnsProxy' &&
      bulkManagementTableModals.activeModal !== 'holdingsProxy'
    ) {
      setOpenedSecondaryId(undefined);
    }
  }, [bulkManagementTableModals, bulkManagementTableModals.activeModal]);

  const onClearSelected = useCallback(() => {
    setSelectedFundIds([]);
    gridRef.current?.api.deselectAll();
  }, []);

  const [selectedDate, setSelectedDate] = useState<string | undefined>();

  const onSetSetSelectedDate = useCallback(
    (date: string | undefined) => {
      onClearSelected();
      setSelectedDate(date);
      analyticsService.dropdownItemSSelected({
        label: 'PORTFOLIO_ALLOCATIONS_AS_OF_DATE',
        type: 'primary dropdown',
        selectType: 'single-select',
        selection: moment.utc(Number(date)).format('MM/DD/YYYY').toString(),
      });
    },
    [onClearSelected],
  );

  const allocationDates = useMemo(() => getPortfolioAllocDates(subject?.portfolio), [subject?.portfolio]);
  const selectedAllocationDate = selectedDate ?? String(allocationDates?.[allocationDates.length - 1]);

  const rangeData = useMemo(() => {
    if (!rangeAnalysis) {
      return undefined;
    }

    return getRangeLogic(
      true,
      allocationDates.at(0),
      getPortfolioMaxAllocationDate(subject?.portfolio, primaryFactorLens?.earliestEndDate ?? now()),
      rangeAnalysis,
      primaryRangeAnalysis,
      {
        short: true,
        includeCashRate: !!findInPortfolioBy(
          subject?.portfolio,
          (node) => isCashNode(node) && (node.cashBalance ?? 0) >= 0.0,
        ),
        includeReferenceRate: !!findInPortfolioBy(
          subject?.portfolio,
          (node) => isCashNode(node) && (node.cashBalance ?? 0) < 0.0,
        ),
      },
    );
  }, [rangeAnalysis, allocationDates, subject?.portfolio, primaryFactorLens?.earliestEndDate, primaryRangeAnalysis]);

  const onSelectionChanged = useCallback((event: SelectionChangedEvent<BulkManageRow, unknown>) => {
    const rows = event.api.getSelectedRows();
    setSelectedFundIds(compact(rows.map((row) => isBulkManageFundRow(row) && row.investment?.id)));
  }, []);
  const onProxyChange = useCallback(
    (_: ProxyMetaWithName[], failedToProxyFunds: (FundToProxy | FundToBulkProxy)[]) => {
      if (openedSecondaryId) {
        // If we were open due to a secondary ID, don't change the selection.
        return;
      }

      const gridApi = gridRef.current?.api;
      if (gridApi) {
        const fundIds = failedToProxyFunds?.map((f) => f.id) ?? [];
        const fundIdSet = new Set(fundIds);
        gridApi.deselectAll();
        gridApi.forEachNode((rowNode) => {
          if (fundIdSet.has(rowNode.data.investment?.id)) {
            rowNode.setSelected(true);
          }
        });
      }
    },
    [openedSecondaryId],
  );

  const selectedFunds = useMemo(
    () => uniqFunds.filter((fund) => selectedFundIds.includes(fund.id)),
    [selectedFundIds, uniqFunds],
  );

  const rowClassRules = useMemo(
    (): RowClassRules<BulkManageRow> => ({
      [AxisRowClass]: (params) => isBulkManageAxisRow(params.data),
      '[&&]:bg-venn-error-highlight': ({ data }) =>
        rowHasHistoricalError(data) && shouldHighlightRowsWithHistoricalErrors,
    }),
    [shouldHighlightRowsWithHistoricalErrors],
  );

  return {
    bulkManagementTableModals,
    openedSecondaryId,
    uniqFunds,
    selectedFunds,
    onProxyChange,
    rangeAnalysis,
    proxyDataByFund,
    holdingsProxyDataByFund,
    rangeDataByFund,
    benchmarkRange,
    onClearSelected,
    gridRef,
    onSelectionChanged,
    groupHeaderHeight: 30,
    rowClassRules,
    selectedAllocationDate,
    allocationDates,
    selectedDate,
    onSetSetSelectedDate,
    setOpenedSecondaryId,
    setSelectedFundIds,
    showLiquidatedInvestments,
    benchmarkInfo,
    rangeData,
    primaryFactorLens,
    setShowLiquidatedInvestments,
    cashRateRange,
    referenceRateRange,
  };
};
